<template>
  <div class="dashboard-card">
    <div class="dashboard-card__title">Proximo envio programado</div>

    <div v-if="!schedule.exists" class="dashboard-empty">
      Sin envios programados
    </div>

    <template v-else>
      <div class="next-send-summary">
        <div class="next-send-summary__main">
          <div class="next-send-summary__type">{{ schedule.typeLabel }}</div>
          <div class="next-send-summary__datetime">
            <CIcon name="cil-calendar" class="mr-1" />
            {{ schedule.date }} · {{ schedule.time }}
          </div>
        </div>

        <div class="next-send-summary__status" :class="`next-send-summary__status--${schedule.status}`">
          {{ schedule.statusLabel }}
        </div>
      </div>

      <div class="next-send-kpis">
        <div class="next-send-kpi">
          <div class="next-send-kpi__label">Destinatarios</div>
          <div class="next-send-kpi__value">{{ schedule.recipientCount }}</div>
        </div>

        <div class="next-send-kpi">
          <div class="next-send-kpi__label">Tipo</div>
          <div class="next-send-kpi__value next-send-kpi__value--small">{{ schedule.typeLabel }}</div>
        </div>
      </div>

      <div class="next-send-actions">
        <CButton
          v-if="canEdit"
          size="sm"
          color="warning"
          style="color: #fff"
          :disabled="busy"
          title="Editar"
          @click="openEdit"
        >
          <CIcon name="cil-pencil" />
        </CButton>

        <CButton
          v-if="canExecute"
          size="sm"
          color="success"
          :disabled="busy"
          title="Ejecutar ahora"
          @click="executeNow"
        >
          <CIcon name="cil-check-circle" />
        </CButton>

        <CButton
          v-if="canCancel"
          size="sm"
          color="dark"
          :disabled="busy"
          title="Cancelar"
          @click="cancelSchedule"
        >
          <CIcon name="cil-x-circle" />
        </CButton>
      </div>
    </template>

    <ConfirmModal ref="confirmModal" @confirm="confirmAction" />
    <ScheduleSendModal ref="scheduleModal" @saved="handleScheduleSaved" />
  </div>
</template>

<script>
import store from '@/store'
import nextScheduledService from '@/services/dashboard/next_scheduled_delivery_dashboard'
import wsTables from '@/services/events_tables'
import ConfirmModal from '@/components/global/ConfirmModal.vue'
import ScheduleSendModal from '@/components/events/event/detail/scheduled/ScheduleSendModal.vue'

export default {
  name: 'NextScheduledDeliveryCard',
  components: { ConfirmModal, ScheduleSendModal },
  props: {
    eventId: { type: [String, Number], required: true },
    event: { type: Object, default: null },
    refreshToken: { type: Number, default: 0 }
  },
  data() {
    return {
      busy: false,
      hasTables: 0,
      pendingActionType: '',
      pendingActionItem: null,
      schedule: { exists: false }
    }
  },
  watch: {
    eventId: {
      immediate: true,
      handler() {
        this.loadSchedule()
      }
    },
    refreshToken() {
      this.loadSchedule()
    }
  },
  computed: {
    canEdit() {
      return ['scheduled', 'failed'].includes((this.schedule.status || '').toLowerCase())
    },
    canCancel() {
      return ['scheduled', 'failed'].includes((this.schedule.status || '').toLowerCase())
    },
    canExecute() {
      return ['scheduled', 'failed'].includes((this.schedule.status || '').toLowerCase())
    }
  },
  methods: {
    async loadSchedule(options = {}) {
      this.schedule = await nextScheduledService.get(this.eventId, options)
    },
    async loadTables() {
      if (!this.eventId) {
        this.hasTables = 0
        return
      }

      const response = await wsTables.get(this.eventId)
      this.hasTables = response && response.type === 'success' ? (response.data || []).length : 0
    },
    async openEdit() {
      if (!this.schedule.exists || !this.schedule.raw) {
        return
      }

      await this.loadTables()
      this.$refs.scheduleModal.show({
        eventId: this.eventId,
        event: this.event || {},
        hasTables: this.hasTables,
        item: this.schedule.raw,
      })
    },
    async handleScheduleSaved() {
      nextScheduledService.invalidate(this.eventId)
      await this.loadSchedule({ force: true })
      this.$emit('changed')
    },
    async executeNow() {
      if (!this.schedule.exists || !this.schedule.id) {
        return
      }

      this.pendingActionType = 'execute'
      this.pendingActionItem = this.schedule
      this.$refs.confirmModal.show(
        'dark',
        'Ejecutar envio programado',
        'Se ejecutara inmediatamente este envio programado.',
        `<strong>Tipo:</strong> ${this.schedule.typeLabel}<br/><strong>Fecha:</strong> ${this.schedule.date}<br/><strong>Hora:</strong> ${this.schedule.time}`
      )
    },
    async cancelSchedule() {
      if (!this.schedule.exists || !this.schedule.id) {
        return
      }

      this.pendingActionType = 'cancel'
      this.pendingActionItem = this.schedule
      this.$refs.confirmModal.show(
        'dark',
        'Cancelar envio programado',
        'Se cancelara este envio programado.',
        `<strong>Tipo:</strong> ${this.schedule.typeLabel}<br/><strong>Fecha:</strong> ${this.schedule.date}<br/><strong>Hora:</strong> ${this.schedule.time}`
      )
    },
    async confirmAction() {
      if (!this.pendingActionItem || !this.pendingActionType) {
        return
      }

      this.busy = true
      let response = { type: 'error', message: 'No se pudo ejecutar la accion.' }

      if (this.pendingActionType === 'cancel') {
        response = await nextScheduledService.cancel(this.eventId, this.pendingActionItem.id)
      }

      if (this.pendingActionType === 'execute') {
        response = await nextScheduledService.executeNow(this.eventId, this.pendingActionItem.id)
      }

      this.pendingActionItem = null
      this.pendingActionType = ''
      this.busy = false

      this.showToast(response.type, response.message)
      if (response.type === 'success') {
        await this.loadSchedule()
        this.$emit('changed')
      }
    },
    showToast(type, message) {
      store.commit('toast', { color: type, message })
    }
  }
}
</script>

<style scoped>
.dashboard-card {
  height: 100%;
  min-height: 240px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid #dfe7df;
  background: #fff;
  box-shadow: 0 4px 14px rgba(31, 41, 55, 0.08);
}

.dashboard-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

.dashboard-empty {
  font-size: 13px;
  color: #6b7280;
  padding: 16px 0;
  flex: 1;
}

.next-send-summary {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8faf9, #f2f6f4);
}

.next-send-summary__type {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
}

.next-send-summary__datetime {
  margin-top: 4px;
  font-size: 12px;
  color: #4b5563;
}

.next-send-summary__status {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.next-send-summary__status--scheduled {
  background: #fff3cd;
  color: #856404;
}

.next-send-summary__status--executing,
.next-send-summary__status--queued {
  background: #cce5ff;
  color: #004085;
}

.next-send-summary__status--executed {
  background: #d4edda;
  color: #155724;
}

.next-send-summary__status--cancelled,
.next-send-summary__status--failed {
  background: #f8d7da;
  color: #721c24;
}

.next-send-kpis {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.next-send-kpi {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
}

.next-send-kpi__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.next-send-kpi__value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  color: #1f2937;
}

.next-send-kpi__value--small {
  font-size: 14px;
  margin-top: 4px;
}

.next-send-actions {
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

@media (max-width: 767px) {
  .next-send-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .next-send-kpis {
    grid-template-columns: 1fr;
  }

  .next-send-actions {
    justify-content: flex-start;
  }
}
</style>
